import { useCallback, useEffect, useRef, useState } from "react";
import { UseWebSocketParams, UseWebSocketProps } from "./types";

export function useInitWebSocket<T = any>({
  url,
  headers,
  onMessage,
  isConnect = true,
  reconnectInterval = 5000,
  maxReconnectAttempts = Infinity,
}: UseWebSocketParams<T>): UseWebSocketProps<T> {
  const [readyState, setReadyState] = useState<UseWebSocketProps["readyState"]>(
    WebSocket.CLOSED
  );
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const shouldReconnect = useRef(true);
  const instanteousReconnect = useRef(false);

  const connect = () => {
    const encodedHeaders = window.btoa(JSON.stringify(headers));
    const fullUrl = `${url}?info=${encodedHeaders}`;
    const socket = new WebSocket(fullUrl);

    setReadyState(socket.readyState as UseWebSocketProps["readyState"]);

    socket.onopen = () => {
      console.log("WebSocket connected");
      reconnectAttempts.current = 0;
      setReadyState(WebSocket.OPEN);
    };

    socket.onmessage = (event) => {
      onMessage(event);
    };

    socket.onerror = (error) => {
      console.error("WebSocket error", error);
    };

    socket.onclose = (event) => {
      console.warn("WebSocket closed", event.reason);
      setReadyState(WebSocket.CLOSED);
      if (
        !instanteousReconnect.current &&
        shouldReconnect.current &&
        reconnectAttempts.current < maxReconnectAttempts
      ) {
        reconnectAttempts.current++;
        setTimeout(connect, reconnectInterval);
      } else if (instanteousReconnect.current) {
        instanteousReconnect.current = false;
        connect();
      }
    };

    socketRef.current = socket;
  };

  const reconnect = useCallback(() => {
    console.log("Manual reconnect called");

    if (socketRef.current) {
      shouldReconnect.current = true;
      instanteousReconnect.current = true;
      socketRef.current.close();
    }
  }, []);

  const sendJsonMessage = useCallback((data: any) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(data));
    } else {
      console.warn("WebSocket is not open. Message not sent:", data);
    }
  }, []);

  const sendMessage = useCallback((data: any) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(data);
    } else {
      console.warn("WebSocket is not open. Message not sent:", data);
    }
  }, []);

  useEffect(() => {
    if (isConnect) {
      connect();
    }

    return () => {
      shouldReconnect.current = false;
      socketRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnect, url, JSON.stringify(headers)]);

  return {
    sendJsonMessage,
    readyState,
    sendMessage,
    reconnect,
  };
}
